/* eslint-disable @typescript-eslint/no-explicit-any -- the bench drives games of any shape */
import { PHASES, tableFor } from "@brouhaha/engine";
import type { Envelope, Phase, Recording } from "@brouhaha/engine";
import { t, tKey } from "@brouhaha/i18n";
import type { MessageKey } from "@brouhaha/i18n";
import { Button, Card } from "@brouhaha/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";

import { GAMES } from "../games/registry";
import type { GameEntry, GameViews } from "../games/registry";
import { WORKBENCH_ENABLED } from "../lib/flags";
import { GameScreen } from "../workbench/GameScreen";
import type { Seat } from "../workbench/GameScreen";
import { HotSeat } from "../workbench/HotSeat";
import { PhoneFrame } from "../workbench/PhoneFrame";
import { channelName, guestTabSync, hostTabSync, randomId } from "../workbench/tab-sync";
import type { TabSync } from "../workbench/tab-sync";
import { useWorkbench } from "../workbench/use-workbench";
import type { Bench } from "../workbench/use-workbench";

type Search = { session?: string; player?: string };

export const Route = createFileRoute("/workbench")({
  validateSearch: (search: Record<string, unknown>): Search => {
    const out: Search = {};
    if (typeof search.session === "string") out.session = search.session;
    if (typeof search.player === "string") out.player = search.player;
    return out;
  },
  component: WorkbenchRoute,
});

type Mode = "step" | "hot-seat" | "multi-tab";

const STATUS_KEY: Record<string, MessageKey> = {
  playing: "workbench.status.playing",
  paused: "workbench.status.paused",
  vetoed: "workbench.status.vetoed",
  finished: "workbench.status.finished",
};

const PHASE_KEY: Record<Phase, MessageKey> = {
  kickoff: "workbench.phase.kickoff",
  "face-down": "workbench.phase.face-down",
  reveal: "workbench.phase.reveal",
  score: "workbench.phase.score",
  results: "workbench.phase.results",
};

function useViews(entry: GameEntry): GameViews | null {
  const [views, setViews] = useState<GameViews | null>(null);
  useEffect(() => {
    let alive = true;
    setViews(null);
    void entry.views().then((v) => {
      if (alive) setViews(v);
    });
    return () => {
      alive = false;
    };
  }, [entry]);
  return views;
}

function WorkbenchRoute() {
  const search = Route.useSearch();
  if (!WORKBENCH_ENABLED) {
    return (
      <main className="flex flex-1 flex-col gap-6">
        <h1 className="text-xl font-bold">{t("workbench.title")}</h1>
        <Card>{t("workbench.disabled")}</Card>
        <BackButton />
      </main>
    );
  }
  if (search.session) return <GuestTab sessionId={search.session} initialPlayer={search.player} />;
  return <HostBench />;
}

function BackButton() {
  return (
    <Link to="/" className="contents">
      <Button variant="secondary" full tabIndex={-1}>
        {t("workbench.back")}
      </Button>
    </Link>
  );
}

/* ------------------------------------------------------------------------ */

function HostBench() {
  const bench = useWorkbench();
  const views = useViews(bench.entry);
  const [mode, setMode] = useState<Mode>("step");
  const [seat, setSeat] = useState<Seat>("steward");
  const [sessionId] = useState(randomId);

  // Multi-tab: this tab hosts. Hooks read through a ref so the sync sees fresh state.
  const benchRef = useRef<Bench>(bench);
  benchRef.current = bench;
  const syncRef = useRef<TabSync | null>(null);
  useEffect(() => {
    if (mode !== "multi-tab" || typeof BroadcastChannel === "undefined") return undefined;
    const sync = hostTabSync(new BroadcastChannel(channelName(sessionId)), {
      recording: () => benchRef.current.recording,
      apply: (env) => benchRef.current.dispatch(env),
    });
    syncRef.current = sync;
    return () => {
      sync.close();
      syncRef.current = null;
    };
  }, [mode, sessionId]);

  const dispatch = (env: Envelope<any>) => {
    if (mode === "multi-tab" && syncRef.current) syncRef.current.dispatch(env);
    else bench.dispatch(env);
  };

  const phase = bench.entry.game.phase(bench.session.state) as Phase;

  return (
    <main className="flex flex-col gap-8">
      <header>
        <h1 className="text-xl font-bold">{t("workbench.title")}</h1>
        <p className="mt-2 text-sm text-ink-muted">{t("workbench.intro")}</p>
      </header>

      <Setup bench={bench} />

      <ModePicker mode={mode} onChange={setMode} />

      <StatusBar bench={bench} phase={phase} dispatch={dispatch} />

      {mode === "step" && <SeatPicker bench={bench} seat={seat} onChange={setSeat} />}

      {mode === "multi-tab" && <MultiTabHost sessionId={sessionId} bench={bench} />}

      <PhoneFrame label={t("workbench.viewAs")}>
        {mode === "hot-seat" ? (
          <HotSeat
            entry={bench.entry}
            views={views}
            session={bench.session}
            ctx={bench.ctx}
            dispatch={dispatch}
          />
        ) : (
          <GameScreen
            entry={bench.entry}
            views={views}
            session={bench.session}
            ctx={bench.ctx}
            seat={mode === "multi-tab" ? "steward" : seat}
            dispatch={dispatch}
          />
        )}
      </PhoneFrame>

      {mode === "step" && <LegalActions bench={bench} dispatch={dispatch} />}

      <StatePanel bench={bench} />

      <RecordingPanel bench={bench} />

      <BackButton />
    </main>
  );
}

function Setup({ bench }: { bench: Bench }) {
  const gameId = useId();
  const playersId = useId();
  const seedId = useId();
  const [seedDraft, setSeedDraft] = useState(bench.seed);
  useEffect(() => setSeedDraft(bench.seed), [bench.seed]);

  return (
    <Card className="flex flex-col gap-4">
      <div>
        <label htmlFor={gameId} className="block text-sm font-semibold">
          {t("workbench.game")}
        </label>
        <select
          id={gameId}
          className="mt-1 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4"
          value={bench.entry.game.manifest.id}
          onChange={(e) => bench.newGame({ gameId: e.target.value })}
        >
          {GAMES.map((g) => (
            <option key={g.game.manifest.id} value={g.game.manifest.id}>
              {tKey(g.game.manifest.title)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor={playersId} className="block text-sm font-semibold">
            {t("workbench.players")}
          </label>
          <select
            id={playersId}
            className="mt-1 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4"
            value={bench.ctx.players.length}
            onChange={(e) => bench.newGame({ players: Number(e.target.value) })}
          >
            {range(
              bench.entry.game.manifest.players.min,
              bench.entry.game.manifest.players.max,
            ).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor={seedId} className="block text-sm font-semibold">
            {t("workbench.seed")}
          </label>
          <input
            id={seedId}
            className="mt-1 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4 font-mono"
            value={seedDraft}
            onChange={(e) => setSeedDraft(e.target.value)}
            onBlur={() => seedDraft !== bench.seed && bench.newGame({ seed: seedDraft })}
          />
        </div>
      </div>

      <Button
        variant="secondary"
        full
        onClick={() => bench.newGame(seedDraft ? { seed: seedDraft } : {})}
      >
        {t("workbench.newGame")}
      </Button>
    </Card>
  );
}

function ModePicker({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const options: Array<[Mode, MessageKey, MessageKey]> = [
    ["step", "workbench.mode.step", "workbench.mode.stepHelp"],
    ["hot-seat", "workbench.mode.hotSeat", "workbench.mode.hotSeatHelp"],
    ["multi-tab", "workbench.mode.multiTab", "workbench.mode.multiTabHelp"],
  ];
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-lg font-semibold">{t("workbench.mode")}</legend>
      {options.map(([value, label, help]) => (
        <label
          key={value}
          className={`flex min-h-target cursor-pointer items-start gap-3 rounded-2xl border-2 p-3 ${
            mode === value ? "border-ember bg-surface" : "border-line"
          }`}
        >
          <input
            type="radio"
            name="mode"
            value={value}
            checked={mode === value}
            onChange={() => onChange(value)}
            className="mt-1 size-6 accent-ember"
          />
          <span>
            <span className="block font-semibold">{t(label)}</span>
            <span className="block text-sm text-ink-muted">{t(help)}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function StatusBar({
  bench,
  phase,
  dispatch,
}: {
  bench: Bench;
  phase: Phase;
  dispatch: (env: Envelope<any>) => void;
}) {
  const jumpId = useId();
  const [target, setTarget] = useState<Phase>("reveal");
  const [jumpFailed, setJumpFailed] = useState(false);
  const status = bench.session.status;

  return (
    <Card className="flex flex-col gap-4">
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-ink-muted">{t("workbench.status")}</dt>
        <dd className="font-semibold">{t(STATUS_KEY[status] ?? "workbench.status.playing")}</dd>
        <dt className="text-ink-muted">{t("workbench.phase")}</dt>
        <dd className="font-semibold">{t(PHASE_KEY[phase])}</dd>
      </dl>

      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="danger"
          onClick={() => dispatch({ kind: "veto", by: bench.ctx.stewardId })}
        >
          {t("workbench.veto")}
        </Button>
        {status === "paused" ? (
          <Button
            variant="secondary"
            onClick={() => dispatch({ kind: "resume", by: bench.ctx.stewardId })}
          >
            {t("workbench.resume")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => dispatch({ kind: "pause", by: bench.ctx.stewardId })}
          >
            {t("workbench.pause")}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={bench.undo}
          disabled={bench.recording.envelopes.length === 0}
        >
          {t("workbench.undo")}
        </Button>
        <Button variant="ghost" onClick={bench.randomStep} disabled={status !== "playing"}>
          {t("workbench.randomStep")}
        </Button>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label htmlFor={jumpId} className="block text-sm font-semibold">
            {t("workbench.jumpTo")}
          </label>
          <select
            id={jumpId}
            className="mt-1 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4"
            value={target}
            onChange={(e) => setTarget(e.target.value as Phase)}
          >
            {PHASES.map((p) => (
              <option key={p} value={p}>
                {t(PHASE_KEY[p])}
              </option>
            ))}
          </select>
        </div>
        <Button
          variant="secondary"
          onClick={() => setJumpFailed(!bench.jumpTo(target))}
          disabled={status !== "playing"}
        >
          {t("workbench.jump")}
        </Button>
      </div>
      {jumpFailed && (
        <p role="alert" className="text-sm text-rose">
          {t("workbench.jumpFailed")}
        </p>
      )}
    </Card>
  );
}

function SeatPicker({
  bench,
  seat,
  onChange,
}: {
  bench: Bench;
  seat: Seat;
  onChange: (s: Seat) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold">
        {t("workbench.viewAs")}
      </label>
      <select
        id={id}
        className="mt-1 min-h-target w-full rounded-2xl border-2 border-line bg-well px-4"
        value={seat}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="steward">{t("workbench.steward")}</option>
        {bench.ctx.players.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nickname}
          </option>
        ))}
      </select>
    </div>
  );
}

function LegalActions({
  bench,
  dispatch,
}: {
  bench: Bench;
  dispatch: (env: Envelope<any>) => void;
}) {
  const legal =
    bench.session.status === "playing"
      ? bench.entry.game.legalActions(bench.session.state, tableFor(bench.session, bench.ctx))
      : [];
  const name = (by: string) =>
    by === "steward"
      ? t("workbench.steward")
      : (bench.ctx.players.find((p) => p.id === by)?.nickname ?? by);
  return (
    <section aria-labelledby="wb-legal" data-testid="legal-actions" className="flex flex-col gap-2">
      <h2 id="wb-legal" className="text-lg font-semibold">
        {t("workbench.legal")}
      </h2>
      {legal.length === 0 && <p className="text-sm text-ink-muted">{t("workbench.noActions")}</p>}
      {legal.map((l, i) => (
        <Button
          key={i}
          variant="secondary"
          full
          className="justify-between"
          onClick={() => dispatch({ kind: "game", action: l.action, by: l.by })}
        >
          <span>{tKey(l.label)}</span>
          <span className="text-sm text-ink-muted">
            {name(l.by)}
            {l.private ? ` · ${t("workbench.private")}` : ""}
          </span>
        </Button>
      ))}
    </section>
  );
}

function StatePanel({ bench }: { bench: Bench }) {
  const highlights =
    bench.session.status === "finished"
      ? bench.entry.game.highlights(bench.session.state, bench.ctx)
      : [];
  return (
    <section className="flex flex-col gap-3">
      {highlights.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold">{t("workbench.highlights")}</h2>
          <ul className="mt-2 list-disc pl-6">
            {highlights.map((h) => (
              <li key={h}>{tKey(h)}</li>
            ))}
          </ul>
        </Card>
      )}
      <details className="rounded-card border border-line bg-surface p-5">
        <summary className="min-h-target cursor-pointer text-lg font-semibold">
          {t("workbench.state")}
        </summary>
        <pre className="mt-3 overflow-x-auto text-xs leading-snug text-ink-muted">
          {JSON.stringify(
            { status: bench.session.status, seq: bench.session.seq, state: bench.session.state },
            null,
            2,
          )}
        </pre>
      </details>
    </section>
  );
}

function RecordingPanel({ bench }: { bench: Bench }) {
  const scrubId = useId();
  const pasteId = useId();
  const [paste, setPaste] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const total = bench.steps.length;
  const cursor = bench.cursor ?? total - 1;
  const json = JSON.stringify(bench.recording);

  async function copy() {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  function load() {
    try {
      const rec = JSON.parse(paste) as Recording<any>;
      const ok = bench.load(rec);
      setLoadFailed(!ok);
      if (ok) setPaste("");
    } catch {
      setLoadFailed(true);
    }
  }

  return (
    <Card className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t("workbench.recording")}</h2>

      <div>
        <label htmlFor={scrubId} className="block text-sm">
          {t("workbench.scrub", { step: cursor, total: total - 1 })}
        </label>
        <input
          id={scrubId}
          type="range"
          min={0}
          max={total - 1}
          value={cursor}
          onChange={(e) => {
            const v = Number(e.target.value);
            bench.scrub(v >= total - 1 ? null : v);
          }}
          className="mt-2 h-target w-full accent-ember"
        />
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={() => void copy()}>
          {t("workbench.copyRecording")}
        </Button>
        {copied && (
          <span role="status" className="text-sm text-mint">
            {t("workbench.copied")}
          </span>
        )}
      </div>

      <div>
        <label htmlFor={pasteId} className="block text-sm">
          {t("workbench.pasteRecording")}
        </label>
        <textarea
          id={pasteId}
          rows={3}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          className="mt-2 w-full rounded-2xl border-2 border-line bg-well p-3 font-mono text-xs"
        />
        <div className="mt-2 flex items-center gap-3">
          <Button variant="secondary" onClick={load} disabled={paste.trim() === ""}>
            {t("workbench.load")}
          </Button>
          {loadFailed && (
            <span role="alert" className="text-sm text-rose">
              {t("workbench.loadFailed")}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function MultiTabHost({ sessionId, bench }: { sessionId: string; bench: Bench }) {
  return (
    <Card className="flex flex-col gap-3">
      <p>
        <span className="text-ink-muted">{t("workbench.tabRole")} </span>
        <span className="font-semibold">{t("workbench.tabHost")}</span>
      </p>
      <p>
        <span className="text-ink-muted">{t("workbench.tabSession")} </span>
        <span className="font-mono font-semibold" data-testid="session-id">
          {sessionId}
        </span>
      </p>
      <ul className="flex flex-col gap-2">
        {bench.ctx.players.map((p) => (
          <li key={p.id}>
            <Link
              to="/workbench"
              search={{ session: sessionId, player: p.id }}
              target="_blank"
              rel="noreferrer"
              className="contents"
            >
              <Button variant="secondary" full tabIndex={-1} className="justify-between">
                <span>{p.nickname}</span>
                <span className="text-sm text-ink-muted">{t("workbench.tabOpenAnother")}</span>
              </Button>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ------------------------------------------------------------------------ */

function GuestTab({
  sessionId,
  initialPlayer,
}: {
  sessionId: string;
  initialPlayer: string | undefined;
}) {
  const bench = useWorkbench();
  const views = useViews(bench.entry);
  const [connected, setConnected] = useState(false);
  const [seat, setSeat] = useState<Seat>(initialPlayer ?? "steward");
  const benchRef = useRef<Bench>(bench);
  benchRef.current = bench;
  const syncRef = useRef<TabSync | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    const sync = guestTabSync(new BroadcastChannel(channelName(sessionId)), randomId(), {
      onSnapshot: (rec) => {
        benchRef.current.load(rec);
        setConnected(true);
      },
      onApplied: (env) => {
        benchRef.current.dispatch(env);
        setConnected(true);
      },
    });
    syncRef.current = sync;
    return () => {
      sync.close();
      syncRef.current = null;
    };
  }, [sessionId]);

  const dispatch = (env: Envelope<any>) => syncRef.current?.dispatch(env);

  return (
    <main className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-bold">{t("workbench.title")}</h1>
        <p className="mt-2 text-sm text-ink-muted">
          <span>{t("workbench.tabSession")} </span>
          <span className="font-mono font-semibold">{sessionId}</span>
        </p>
      </header>

      <p role="status" className={connected ? "text-mint" : "text-ink-muted"}>
        {connected ? t("workbench.tabConnected") : t("workbench.tabWaiting")}
      </p>

      {connected && <SeatPicker bench={bench} seat={seat} onChange={setSeat} />}

      <PhoneFrame label={t("workbench.viewAs")}>
        {connected && (
          <GameScreen
            entry={bench.entry}
            views={views}
            session={bench.session}
            ctx={bench.ctx}
            seat={seat}
            dispatch={dispatch}
          />
        )}
      </PhoneFrame>

      <BackButton />
    </main>
  );
}

function range(min: number, max: number): number[] {
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}
